import { PageContainer, DetailsContainer, LeftSection, RightSection, StyledDivider, CollectionTitle, CollectionDescription } from "./style-component";
import CollectionBanner from "@/components/collection-banner/collection-banner";
import { CollectionDetails } from "@/components/collection-banner/components/collection-details";
import { GridCards } from "@/components/grid-cards/grid-cards";
import CollectibleCard from "@/components/collectible-card/collectible-card";
import SectionWithHeadings from "@/components/section-with-headings/section-with-headings";
import { useParams } from "react-router-dom";
import { getCollection } from "@/services/collections/collections";
import { Collection } from "@/types/collection/collection-type";
import useFetch from "@/hook/useFetch/useFetch";
import { getCollectibles } from "@/services/collectibles/collectibles";
import { Collectible } from "@/types/collectible/collectible-type";
import { SpaceshipLoader } from "@/components/spaceship-loader/spaceship-loader";
import { useUserContext } from "@/contexts/user/useUserContext";
import SEOHelmet from "@/components/SEOHelmet/SEOHelmet";
import { useRef, useEffect } from "react";
import { amplitudeAddTrack, determineProjectName } from "@/utils/amplitud/event-track";


const CollectionPage: React.FC = () => {
    const { collectionId } = useParams<{ collectionId: string }>();

    const {
        data: collectionsResponse,
        loading: collectionLoading,
    } = useFetch<Collection[], string>(
        () => getCollection(collectionId ?? '')
    );

    const collection = collectionsResponse[0];
    const { user } = useUserContext();

    const {
        data: collectiblesData,
        loading: collectiblesLoading,
    } = useFetch<Collectible[], object>(getCollectibles, { owner: user?.main_key ?? '', collection_key: collectionId ?? '' });

    const firstRenderAmplitude = useRef<boolean>(true);
    useEffect(() => {
        if (collection?.name && firstRenderAmplitude.current) {
            firstRenderAmplitude.current = false;
            amplitudeAddTrack(determineProjectName(`Colección: ${collection?.name}`), {
                title: determineProjectName(`Colección: ${collection?.name}`),
            })
        }
    }, [collection]);

    if (collectionLoading || collectiblesLoading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                <SpaceshipLoader height="200px" />
            </div>
        );
    }

    if (!collection) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', color: 'white', minHeight: '50vh' }}>
                No se encontró la colección
            </div>
        );
    }

    const getSupply = () => {
        try {
            const details = typeof collection.extra_details === 'string'
                ? JSON.parse(collection.extra_details)
                : collection.extra_details;

            return details?.supply?.toLocaleString() ?? '0';
        } catch (error) {
            console.error(error)
            return '0';
        }
    };

    const truncateAddress = (address: string) => {
        if (address.length <= 12) return address;
        return `${address.slice(0, 6)}...${address.slice(-4)}`;
    };

    return (
        <PageContainer>
            <SEOHelmet
                metadata={{
                    title: determineProjectName(`Colección: ${collection?.name}`),
                    description: determineProjectName(`${collection?.description || ''}`)
                }}
            />
            <CollectionBanner
                name={collection.name}
                banner_url={collection.banner_url_collection}
                thumb_url={collection.thumb_url ?? ''}
            />
            <DetailsContainer>
                <LeftSection>

                    <CollectionTitle>{collection.name}</CollectionTitle>
                    <CollectionDescription>{collection.description}</CollectionDescription>
                </LeftSection>
                <StyledDivider />
                <RightSection>
                    <CollectionDetails
                        name={collection.name}
                        description={collection.description}
                        blockchain_name={collection.blockchain_name}
                        collection_key={collection.collection_key}
                        thumb_url={collection.thumb_url ?? ''}
                        getSupply={getSupply}
                        truncateAddress={truncateAddress}
                    />
                </RightSection>

            </DetailsContainer>
            <SectionWithHeadings title="Coleccionables" titleColor="default" style={{ marginTop: '32px' }}>
                {collectiblesData?.length ? (
                    <GridCards
                        items={collectiblesData}
                        variant="collectible"
                        renderItem={(collectible) => (
                            <CollectibleCard
                                name={collectible?.metadata?.json_data?.name ?? ''}
                                thumb_url={collectible.thumb_url ?? ''}
                                token_id={collectible.token_id ?? 0}
                            />
                        )}
                    />
                ) : (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', color: 'white' }}>
                        No hay coleccionables disponibles
                    </div>
                )}
            </SectionWithHeadings>
        </PageContainer>
    );
};

export default CollectionPage;